<app-loading-spinner *ngIf="loading; else not_loading"></app-loading-spinner>
<ng-template #not_loading>

  <div class="filters-section mb-4" [class.disabled]="filtering">
    <div class="row">
      <div class="col-md-3">
        <label>Calculation Date</label>
        <app-datepicker [(model)]="calculationDate" (modelChange)="onDateChanged()" [disabled]="ignoreLicenseDates || filtering"></app-datepicker>
        <div class="form-check mt-2">
          <input class="form-check-input" type="checkbox" id="ignoreLicenseDates" 
                 [(ngModel)]="ignoreLicenseDates" (change)="onIgnoreLicenseDatesChanged(ignoreLicenseDates)"
                 [disabled]="filtering">
          <label class="form-check-label" for="ignoreLicenseDates">
            Ignore License Dates
          </label>
        </div>
      </div>
      <div class="col-md-3">
        <label>Grace Period</label>
        <div class="form-check">
          <input class="form-check-input" type="checkbox" id="excludeGracePeriod" 
                 [(ngModel)]="excludeGracePeriod" (change)="updateFilteredAndSortedLicenses()"
                 [disabled]="filtering">
          <label class="form-check-label" for="excludeGracePeriod">
            Exclude licenses in grace period
          </label>
        </div>
      </div>
      <div class="col-md-3">
        <label>Purchase Type</label>
        <select class="form-control" [(ngModel)]="selectedPurchaseType" (change)="updateFilteredAndSortedLicenses()" [disabled]="filtering">
          <option value="">All Purchase Types</option>
          <option *ngFor="let type of availablePurchaseTypes" [value]="type">{{ type }}</option>
        </select>
      </div>
      <div class="col-md-3">
        <label>Purchase Level</label>
        <select class="form-control" [(ngModel)]="selectedPurchaseLevel" (change)="updateFilteredAndSortedLicenses()" [disabled]="filtering">
          <option value="">All Purchase Levels</option>
          <option *ngFor="let level of availablePurchaseLevels" [value]="level">{{ level }}</option>
        </select>
      </div>
    </div>
    <div class="row mt-3">
      <div class="col-12">
        <div class="filter-status" *ngIf="isFiltering">
          <small class="text-muted">
            <i class="fas fa-filter"></i>
            Showing {{ filteredAndSortedLicenses?.length || 0 }} of {{ allLicenses?.length || 0 }} licenses
          </small>
        </div>
      </div>
    </div>
  </div>

  <!-- Summary section -->
  <div class="summary-section mb-4" *ngIf="filteredAndSortedLicenses && filteredAndSortedLicenses.length" [class.disabled]="filtering">
    <div class="row">
      <div class="col-md-3">
        <div class="summary-item">
          <div class="summary-label">License Count</div>
          <div class="summary-value">{{ formatNumber(totalQuantity) }}</div>
        </div>
      </div>
      <div class="col-md-3">
        <div class="summary-item">
          <div class="summary-label">Total Purchase Amount</div>
          <div class="summary-value">{{ formatCurrencyNoCents(totalPurchaseAmount) }}</div>
        </div>
      </div>
      <div class="col-md-3">
        <div class="summary-item">
          <div class="summary-label">Total ARR</div>
          <div class="summary-value">{{ formatCurrencyNoCents(totalARR) }}</div>
        </div>
      </div>
      <div class="col-md-3">
        <div class="summary-item">
          <div class="summary-label">Average Cost Per License</div>
          <div class="summary-value">{{ formatCurrencyNoCents(averageCostPerLicense) }}</div>
        </div>
      </div>
    </div>
  </div>



  <div class="table-wrapper">
    <div class="table-container">
      <!-- Table Controls -->
      <div class="d-flex justify-content-between align-items-center mb-3" [class.disabled]="filtering">
        <div>
          <button class="btn btn-outline-primary btn-sm" (click)="licenseChangeAnalysisModal.openModal()" [disabled]="filtering">
            <i class="fas fa-chart-line mr-1"></i> License Change Analysis
          </button>
        </div>
        <div class="d-flex align-items-center">
          <div ngbDropdown placement="bottom-right" class="mr-2" [autoClose]="'outside'" #columnSettingsDropdown>
            <button class="btn btn-outline-secondary btn-sm dropdown-toggle" type="button" 
                    ngbDropdownToggle [disabled]="filtering">
              <i class="fas fa-cog mr-1"></i> Column Settings
            </button>
            <div ngbDropdownMenu class="dropdown-menu-right">
              <h6 class="dropdown-header">Toggle Columns</h6>
              <div class="dropdown-item" *ngFor="let column of columnConfig">
                <div class="form-check">
                  <input class="form-check-input" type="checkbox" 
                         [id]="'col-' + column.id" 
                         [checked]="column.visible"
                         (change)="toggleColumnVisibility(column.id)">
                  <label class="form-check-label" [for]="'col-' + column.id">
                    {{ column.label }}
                  </label>
                </div>
              </div>
              <div class="dropdown-divider"></div>
              <button class="dropdown-item" (click)="selectAllColumns()">
                <i class="fas fa-check-square mr-1"></i> Select All
              </button>
              <button class="dropdown-item" (click)="deselectAllColumns()">
                <i class="fas fa-square mr-1"></i> Deselect All
              </button>
              <button class="dropdown-item" (click)="resetToDefaultColumns()">
                <i class="fas fa-undo mr-1"></i> Reset to Default
              </button>
            </div>
          </div>
          <div *ngIf="isSeanUser() && organizationId && organizationId !== 422">
            <button class="btn btn-primary btn-sm" (click)="openNewLicenseModal()" [disabled]="filtering">
              <i class="fas fa-plus mr-1"></i> Add New Licenses
            </button>
          </div>
        </div>
      </div>

      <!-- Show spinner during filtering -->
      <div *ngIf="filtering" class="text-center py-4">
        <app-loading-spinner [text]="'Filtering licenses...'"></app-loading-spinner>
      </div>

      <!-- Show table when not filtering -->
      <table class="table" *ngIf="!filtering && filteredAndSortedLicenses && filteredAndSortedLicenses.length; else no_licenses">
        <thead>
          <th [class.hidden-column]="!isPurchasingOrganizationDescriptionVisible" class="sortable">
            <app-sort-toggle columnName="PurchasingOrganizationDescription" (orderChange)="updateSort($event)">
              Purchasing Organization
            </app-sort-toggle>
          </th>
          <th [class.hidden-column]="!isQuantityVisible" class="sortable">
            <app-sort-toggle columnName="Quantity" (orderChange)="updateSort($event)">
              Quantity
            </app-sort-toggle>
          </th>
          <th [class.hidden-column]="!isPurchaseDateVisible" class="sortable">
            <app-sort-toggle columnName="PurchaseDate" (orderChange)="updateSort($event)">
              Purchase Date
            </app-sort-toggle>
          </th>
          <th [class.hidden-column]="!isLicenseEndDateVisible" class="sortable">
            <app-sort-toggle columnName="LicenseEndDate" (orderChange)="updateSort($event)">
              License End Date
            </app-sort-toggle>
          </th>
          <th [class.hidden-column]="!isIsInGracePeriodVisible" class="sortable">
            <app-sort-toggle columnName="IsInGracePeriod" (orderChange)="updateSort($event)">
              Status
            </app-sort-toggle>
          </th>
          <th [class.hidden-column]="!isPurchaseDollarAmountVisible" class="sortable">
            <app-sort-toggle columnName="PurchaseDollarAmount" (orderChange)="updateSort($event)">
              Purchase Amount
            </app-sort-toggle>
          </th>
          <th [class.hidden-column]="!isLicenseLengthYearsVisible" class="sortable">
            <app-sort-toggle columnName="LicenseLengthYears" (orderChange)="updateSort($event)">
              License Length (Years)
            </app-sort-toggle>
          </th>
          <th [class.hidden-column]="!isARRVisible" class="sortable">
            <app-sort-toggle columnName="ARR" (orderChange)="updateSort($event)">
              ARR
            </app-sort-toggle>
          </th>
          <th [class.hidden-column]="!isCostPerLicenseVisible" class="sortable">
            <app-sort-toggle columnName="CostPerLicense" (orderChange)="updateSort($event)">
              Cost Per License
            </app-sort-toggle>
          </th>
          <th [class.hidden-column]="!isLicenseStartDateVisible" class="sortable">
            <app-sort-toggle columnName="LicenseStartDate" (orderChange)="updateSort($event)">
              License Start Date
            </app-sort-toggle>
          </th>
          <th [class.hidden-column]="!isLicensePurchaseTypeVisible" class="sortable">
            <app-sort-toggle columnName="LicensePurchaseType" (orderChange)="updateSort($event)">
              Purchase Type
            </app-sort-toggle>
          </th>
          <th [class.hidden-column]="!isPurchaseLevelVisible" class="sortable">
            <app-sort-toggle columnName="PurchaseLevel" (orderChange)="updateSort($event)">
              Purchase Level
            </app-sort-toggle>
          </th>
          <th [class.hidden-column]="!isPurchaseOrderIDVisible" class="sortable">
            <app-sort-toggle columnName="PurchaseOrderID" (orderChange)="updateSort($event)">
              Purchase Order ID
            </app-sort-toggle>
          </th>
          <th [class.hidden-column]="!isDealIDVisible" class="sortable">
            <app-sort-toggle columnName="DealID" (orderChange)="updateSort($event)">
              Deal ID
            </app-sort-toggle>
          </th>
          <th [class.hidden-column]="!isInvoiceIDVisible" class="sortable">
            <app-sort-toggle columnName="InvoiceID" (orderChange)="updateSort($event)">
              Invoice ID
            </app-sort-toggle>
          </th>
          <th [class.hidden-column]="!isNoteVisible" class="icon">
            Note
          </th>
          <th *ngIf="isSeanUser()"></th>
        </thead>
        <tbody>
          <tr *ngFor="let license of filteredAndSortedLicenses" class="hover">
            <!-- Purchasing Organization -->
            <td [class.hidden-column]="!isPurchasingOrganizationDescriptionVisible">
              <a [routerLink]="['/organizations', license.PurchasingOrganizationID]" [queryParams]="{t: 'Licenses'}">
                {{ license.PurchasingOrganizationDescription }}
              </a>
            </td>
            
            <!-- Quantity -->
            <td [class.hidden-column]="!isQuantityVisible">
              {{ formatNumber(license.Quantity) }}
            </td>
            
            <!-- Purchase Date -->
            <td [class.hidden-column]="!isPurchaseDateVisible">
              {{ formatDate(license.PurchaseDate) }}
            </td>
            
            <!-- License End Date -->
            <td [class.hidden-column]="!isLicenseEndDateVisible">
              {{ formatDate(license.LicenseEndDate) }}
            </td>
            
            <!-- In Grace Period -->
            <td [class.hidden-column]="!isIsInGracePeriodVisible">
              <span *ngIf="license.IsInGracePeriod" class="badge badge-warning">Grace Period</span>
              <span *ngIf="!license.IsInGracePeriod && isLicenseExpired(license)" class="badge badge-danger">Expired</span>
              <span *ngIf="!license.IsInGracePeriod && !isLicenseExpired(license)" class="badge badge-success">Active</span>
            </td>
            
            <!-- Purchase Amount -->
            <td [class.hidden-column]="!isPurchaseDollarAmountVisible">
              {{ formatCurrencyNoCents(license.PurchaseDollarAmount) }}
            </td>
            
            <!-- License Length Years -->
            <td [class.hidden-column]="!isLicenseLengthYearsVisible">
              {{ formatNumber(license.LicenseLengthYears) }}
            </td>
            
            <!-- ARR -->
            <td [class.hidden-column]="!isARRVisible">
              {{ formatCurrencyNoCents(license.ARR) }}
            </td>
            
            <!-- Cost Per License -->
            <td [class.hidden-column]="!isCostPerLicenseVisible">
              {{ formatCostPerLicenseNoCents(license) }}
            </td>
            
            <!-- License Start Date -->
            <td [class.hidden-column]="!isLicenseStartDateVisible">
              {{ formatDate(license.LicenseStartDate) }}
            </td>
            
            <!-- Purchase Type -->
            <td [class.hidden-column]="!isLicensePurchaseTypeVisible">
              {{ license.LicensePurchaseType }}
            </td>
            
            <!-- Purchase Level -->
            <td [class.hidden-column]="!isPurchaseLevelVisible">
              {{ license.PurchaseLevel }}
            </td>
            
            <!-- Purchase Order ID -->
            <td [class.hidden-column]="!isPurchaseOrderIDVisible">
              {{ license.PurchaseOrderID || '-' }}
            </td>
            
            <!-- Deal ID -->
            <td [class.hidden-column]="!isDealIDVisible">
              {{ license.DealID || '-' }}
            </td>
            
            <!-- Invoice ID -->
            <td [class.hidden-column]="!isInvoiceIDVisible">
              {{ license.InvoiceID || '-' }}
            </td>
            
            <!-- Note -->
            <td [class.hidden-column]="!isNoteVisible" class="icon">
              <span *ngIf="hasNote(license)" 
                    [ngbTooltip]="getNoteTooltip(license)" 
                    placement="top" 
                    container="body"
                    class="note-icon">
                <i class="fas fa-sticky-note text-info pointer"></i>
              </span>
            </td>
            
            <!-- Edit button (always visible for Sean) -->
            <td class="icon" *ngIf="isSeanUser()">
              <span (click)="onEditLicense(license, $event)" ngbTooltip="Edit">
                <i class="fal fa-pencil-alt pointer"></i>
              </span>
            </td>
          </tr>
        </tbody>
      </table>
      <button class="download-btn" (click)="downloadCSV()" 
              [disabled]="!filteredAndSortedLicenses || filteredAndSortedLicenses.length === 0"
              *ngIf="filteredAndSortedLicenses && filteredAndSortedLicenses.length > 0"
              title="Download CSV">
        <i class="fas fa-download"></i>
      </button>
    </div>
  </div>

  <ng-template #no_licenses>
    <p class="mt-4">
      <ng-container *ngIf="filteredAndSortedLicenses && !filteredAndSortedLicenses.length">
        No licenses found.
      </ng-container>
      <app-loading-spinner *ngIf="!filteredAndSortedLicenses"></app-loading-spinner>
    </p>
  </ng-template>

  <!-- License Change Analysis Modal -->
  <app-license-change-analysis-modal 
    #licenseChangeAnalysisModal
    [organizationId]="organizationId"
    [organizationDescription]="organizationDescription"
    [allLicenses]="allLicenses">
  </app-license-change-analysis-modal>

  <!-- Add or Edit License Modal -->
  <app-add-or-edit-licenses-modal 
    #addOrEditLicensesModal
    [organizationId]="organizationId"
    [organizationDescription]="organizationDescription"
    [allLicenses]="allLicenses"
    (licenseSaved)="onLicenseSaved($event)">
  </app-add-or-edit-licenses-modal>

</ng-template> 